import { Download, Eye, Image as ImageIcon, Pencil, Trash2, Video as VideoIcon } from 'lucide-react';

import { Button } from '../../../../components/ui/button';
import { Checkbox } from '../../../../components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../../components/ui/tooltip';
import { cn } from '../../../../lib/utils';
import { isVideoEntry, type MediaEntry } from '../../lib/mediaEntries';
import { formatPrefixLabel, getPresetPrefix } from '../../lib/presetPrefixes';

interface ImageCardProps {
  disabled: boolean;
  entry: MediaEntry;
  isCover: boolean;
  onDownload: () => void;
  onPreview: () => void;
  onRequestDelete: () => void;
  onRequestRename: () => void;
  onToggleSelect: () => void;
  selected: boolean;
}

const ImageCard = ({
  disabled,
  entry,
  isCover,
  onDownload,
  onPreview,
  onRequestDelete,
  onRequestRename,
  onToggleSelect,
  selected,
}: ImageCardProps) => {
  const preset = getPresetPrefix(entry.name);
  const isVideo = isVideoEntry(entry);
  const renderPreview = () => {
    if (isVideo) {
      if (!entry.publicFileURL) {
        return (
          <div className="flex aspect-[16/9] items-center justify-center bg-brand-navy text-brand-turquoise">
            <VideoIcon className="size-10" />
          </div>
        );
      }

      return (
        <video className="aspect-[16/9] w-full object-cover" controls preload="metadata" src={entry.publicFileURL}>
          <track kind="captions" />
        </video>
      );
    }

    if (entry.publicFileURL) {
      return (
        <button className="block w-full" onClick={onPreview} type="button">
          <img
            alt={entry.name}
            className="aspect-[16/9] w-full object-cover"
            loading="lazy"
            src={entry.publicFileURL}
          />
        </button>
      );
    }

    return (
      <div className="flex aspect-[16/9] items-center justify-center bg-brand-light text-brand-vivid">
        <ImageIcon className="size-10" />
      </div>
    );
  };

  return (
    <article
      className={cn(
        'min-w-0 overflow-hidden rounded-2xl border border-border-subtle bg-bg-surface transition-colors',
        selected && 'border-border-info bg-surface-info'
      )}
    >
      <div className="relative overflow-hidden bg-brand-light">
        <div className="absolute left-3 top-3 z-10">
          <Checkbox
            aria-label={`Seleccionar ${entry.name}`}
            checked={selected}
            className="border-bg-surface bg-bg-surface/90 data-[state=checked]:border-brand-vivid data-[state=checked]:bg-brand-vivid"
            onCheckedChange={onToggleSelect}
          />
        </div>
        {preset && (
          <span className="absolute bottom-3 left-3 z-10 rounded-full bg-brand-vivid px-3 py-1 text-[10px] font-extrabold uppercase text-text-inverse">
            {formatPrefixLabel(preset)}
          </span>
        )}
        {isCover && (
          <span className="absolute right-3 top-3 z-10 rounded-full bg-brand-navy px-3 py-1 text-[11px] font-extrabold text-text-inverse">
            Portada
          </span>
        )}
        {renderPreview()}
      </div>
      <div className="flex min-h-[118px] flex-col gap-3 px-4 py-3.5">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <p className="m-0 text-[10px] font-extrabold uppercase text-text-subtle">Título del archivo</p>
          <Tooltip>
            <TooltipTrigger asChild>
              <p className="m-0 min-w-0 cursor-default truncate text-sm font-bold text-text-default">{entry.name}</p>
            </TooltipTrigger>
            <TooltipContent className="max-w-72 break-words">{entry.name}</TooltipContent>
          </Tooltip>
        </div>
        <div className="flex min-h-7 items-center justify-end gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <Button
                  aria-label={`Previsualizar ${entry.name}`}
                  onClick={onPreview}
                  size="icon-xs"
                  type="button"
                  variant="ghost"
                >
                  <Eye className="size-4 text-text-muted" />
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>Previsualizar</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <Button
                  aria-label={`Descargar ${entry.name}`}
                  disabled={!entry.publicFileURL}
                  onClick={onDownload}
                  size="icon-xs"
                  type="button"
                  variant="ghost"
                >
                  <Download className="size-4 text-text-muted" />
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>Descargar</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <Button
                  aria-label={`Editar título de ${entry.name}`}
                  disabled={disabled}
                  onClick={onRequestRename}
                  size="icon-xs"
                  type="button"
                  variant="ghost"
                >
                  <Pencil className="size-4 text-text-muted" />
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>Editar nombre</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <Button
                  aria-label={`Eliminar ${entry.name}`}
                  disabled={disabled}
                  onClick={onRequestDelete}
                  size="icon-xs"
                  type="button"
                  variant="ghost"
                >
                  <Trash2 className="size-4 text-error" />
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>Eliminar</TooltipContent>
          </Tooltip>
        </div>
      </div>
    </article>
  );
};

export default ImageCard;
